import { MIN_LZ_RECEIVE_GAS } from '../config/oft';

/**
 * Builds the LayerZero v2 `extraOptions` bytes for an OFT send.
 *
 * LZ v2 Executor Options encoding (per the on-chain enforced options format):
 *   Header: 0x0003 (OPTIONS_TYPE_3)
 *   Each worker option:
 *     workerId:   uint8  (0x01 = Executor)
 *     optionSize: uint16 (byte length of optionType + optionData)
 *     optionType: uint8  (0x01 = lzReceive, 0x02 = nativeDrop)
 *     optionData: bytes
 *
 * Verified against on-chain enforcedOptions(30101, 1) on Ink OFT which returned:
 *   0x00030100110100000000000000000000000000013880
 *         ^^ ^^^^ ^^ ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 *       wkr  size type  gas (80000 = 0x13880)
 */
export function buildExtraOptions(
  gasAirdrop?: { amount: bigint; recipient: `0x${string}` },
): `0x${string}` {
  if (gasAirdrop) {
    return buildExtraOptionsWithNativeDrop(gasAirdrop.amount, gasAirdrop.recipient);
  }
  return buildExtraOptionsLzReceiveOnly();
}

/**
 * lzReceive only:
 *   0x0003                                         — OPTIONS_TYPE_3
 *   01                                             — workerId (Executor)
 *   0011                                           — optionSize (17 bytes)
 *   01                                             — optionType (lzReceive)
 *   00000000000000000000000000013880                — gas uint128 (80000)
 */
function buildExtraOptionsLzReceiveOnly(): `0x${string}` {
  const gasHex = MIN_LZ_RECEIVE_GAS.toString(16).padStart(32, '0');
  return `0x000301001101${gasHex}` as `0x${string}`;
}

/**
 * lzReceive + nativeDrop:
 *   0x0003                                         — OPTIONS_TYPE_3
 *   01 0011 01 [gas 16bytes]                       — lzReceive option
 *   01 0031 02 [amount 16bytes] [receiver 32bytes] — nativeDrop option
 *
 * nativeDrop: optionSize = 1 (type) + 16 (amount) + 32 (receiver) = 49 = 0x0031
 */
function buildExtraOptionsWithNativeDrop(
  amount: bigint,
  recipient: `0x${string}`,
): `0x${string}` {
  const gasHex = MIN_LZ_RECEIVE_GAS.toString(16).padStart(32, '0');
  const lzReceivePart = `01001101${gasHex}`;

  const amountHex = amount.toString(16).padStart(32, '0');
  const recipientHex = recipient.slice(2).toLowerCase().padStart(64, '0');
  const nativeDropPart = `01003102${amountHex}${recipientHex}`;

  return `0x0003${lzReceivePart}${nativeDropPart}` as `0x${string}`;
}

/**
 * Returns the native drop amount for gas airdrop.
 * This is added to the lzNativeFee as msg.value in the OFT send.
 */
export function getNativeDropAmount(
  gasAirdrop?: { amount: bigint; recipient: `0x${string}` },
): bigint {
  return gasAirdrop?.amount ?? BigInt("0");
}
